import React, { useState, useEffect } from "react";
import { useAuth } from "../../../context/AuthContext";
import { updateUserProfile } from "../../../utils/userApi";
import LocationInput from "../../../helper/locationInput";
import {
  PROGRAM_CATEGORIES,
  SELECTABLE_PROGRAM_CATEGORIES,
} from "../../../data/tutorPrograms";
// import { TEACHING_MODES } from "../../tutorRegistration/Step2"; // Import from Step2 or duplicate if needed
import StatusModal from "../../../components/modals/statusModal/StatusModal";
import { toast } from "react-hot-toast";
import { findUnusualRateWarnings } from "../../../utils/rateThresholds";

const ProgramForm = ({ onCancel }) => {
  const { user, updateUser } = useAuth();

  const [isSaving, setIsSaving] = useState(false);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "success",
    message: "",
    subtext: "",
  });

  // 2. Add migration inside useState initializer — remaps old selections into new groups
  const migrateCategories = (oldCategories = [], oldDetails = {}) => {
    const MIGRATION_MAP = {
      "JAMB Prep – Science": "JAMB",
      "JAMB Prep – Arts": "JAMB",
      "JAMB Prep – Commercial": "JAMB",
      "WAEC/NECO Prep – Science": "WAEC/NECO",
      "WAEC/NECO Prep – Arts": "WAEC/NECO",
      "WAEC/NECO Prep – Commercial": "WAEC/NECO",
      "Adult Literacy (Reading, Writing, Spelling, Speaking)":
        "Adult Education",
      "Basic & Secondary Education": "WAEC/NECO",
      "Adult & Foundational Education": "Adult Education",
      "University Entrance & Admission Prep": "JAMB",
    };

    const migratedCategories = [];
    const migratedDetails = {};

    oldCategories.forEach((cat) => {
      const newGroup = MIGRATION_MAP[cat] ?? null;
      // Only migrate into selectable groups; drop others entirely
      if (!newGroup || !SELECTABLE_PROGRAM_CATEGORIES.includes(newGroup))
        return;
      if (!migratedCategories.includes(newGroup)) {
        migratedCategories.push(newGroup);
        migratedDetails[newGroup] = {
          courses: [],
          customCourse: "",
          rate: "",
          period: "hour",
        };
      }
    });

    // Preserve any that are already using the new group names
    SELECTABLE_PROGRAM_CATEGORIES.forEach((group) => {
      if (
        oldCategories.includes(group) &&
        !migratedCategories.includes(group)
      ) {
        migratedCategories.push(group);
        migratedDetails[group] = oldDetails[group] ?? {
          courses: [],
          customCourse: "",
          rate: "",
          period: "hour",
        };
      } else if (migratedCategories.includes(group) && oldDetails[group]) {
        // Merge any existing detail if already present
        migratedDetails[group] = {
          ...migratedDetails[group],
          ...oldDetails[group],
        };
      }
    });

    return { migratedCategories, migratedDetails };
  };

  // Harmonized to exactly 3 modes everywhere (frontend, admin, backend) —
  // see scripts/migrateTeachingModes.js for the data migration from the
  // old 6-value scheme these replace.
  const TEACHING_MODES = [
    {
      value: "online",
      label: "Online",
      icon: "🎥",
      desc: "Real-time virtual sessions with students",
    },
    {
      value: "hybrid",
      label: "Hybrid",
      icon: "🔀",
      desc: "Combination of online and physical classes",
    },
    {
      value: "physical",
      label: "Physical",
      icon: "🏫",
      desc: "Group or individual sessions in a physical centre",
    },
  ];

  // Initialize with existing data
  // const [formData, setFormData] = useState({
  //   selectedCategories: user?.selectedCategories || [],
  //   categoryDetails: user?.categoryDetails || {},
  //   teachingModes: user?.teachingModes || [],
  //   teachingModeOther: user?.teachingModeOther || "",
  //   location: user?.location || {},
  // });

  // 3. Use migration in useState
  const { migratedCategories, migratedDetails } = migrateCategories(
    user?.selectedCategories,
    user?.categoryDetails,
  );

  const [formData, setFormData] = useState({
    selectedCategories: migratedCategories,
    categoryDetails: migratedDetails,
    teachingModes: user?.teachingModes || [],
    teachingModeOther: user?.teachingModeOther || "",
    location: user?.location || {},
    offersIntroSession: user?.offersIntroSession || false,
  });

  // Add a new category
  const toggleCategory = (groupName) => {
    const exists = formData.selectedCategories.includes(groupName);

    setFormData((prev) => {
      let updatedCategories = [...prev.selectedCategories];
      let updatedDetails = { ...prev.categoryDetails };

      if (exists) {
        updatedCategories = updatedCategories.filter((g) => g !== groupName);
        delete updatedDetails[groupName];
      } else {
        updatedCategories.push(groupName);
        updatedDetails[groupName] = {
          courses: [],
          customCourse: "",
          rate: "",
          period: "hour",
        };
      }

      return {
        ...prev,
        selectedCategories: updatedCategories,
        categoryDetails: updatedDetails,
      };
    });
  };

  const updateCategoryDetail = (groupName, newData) => {
    setFormData((prev) => ({
      ...prev,
      categoryDetails: {
        ...prev.categoryDetails,
        [groupName]: { ...prev.categoryDetails[groupName], ...newData },
      },
    }));
  };

  const toggleTeachingMode = (mode) => {
    setFormData((prev) => {
      const exists = prev.teachingModes.includes(mode);
      return {
        ...prev,
        teachingModes: exists
          ? prev.teachingModes.filter((m) => m !== mode)
          : [...prev.teachingModes, mode],
      };
    });
  };

  const handleSave = async () => {
    const rateWarnings = findUnusualRateWarnings(formData.categoryDetails);
    if (rateWarnings.length > 0) {
      const confirmed = window.confirm(
        `${rateWarnings.join("\n")}\n\nUnusually low rates get flagged for admin review. Save anyway?`,
      );
      if (!confirmed) return;
    }

    try {
      setIsSaving(true);

      const payload = {
        selectedCategories: formData.selectedCategories,
        categoryDetails: formData.categoryDetails,
        teachingModes: formData.teachingModes,
        teachingModeOther: formData.teachingModeOther,
        location: formData.location,
        offersIntroSession: formData.offersIntroSession,
      };

      const res = await updateUserProfile(payload, user?.token);

      updateUser(res.user);

      setStatusModal({
        open: true,
        type: "success",
        message: "Program Updated Successfully!",
        subtext: "Your teaching programs and rates have been updated.",
      });
    } catch (err) {
      console.error(err);
      setStatusModal({
        open: true,
        type: "error",
        message: "Update Failed",
        subtext: "Could not update your program details. Please try again.",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="form-grid">
      {/* Teaching Modes */}
      <div className="form-group">
        <label className="form-label required">Teaching Modes</label>
        <div className="teaching-modes-grid" style={{ marginBottom: "20px" }}>
          {TEACHING_MODES.map((mode) => (
            <button
              key={mode.value}
              type="button"
              className={`teaching-mode-card ${formData.teachingModes.includes(mode.value) ? "selected" : ""}`}
              onClick={() => toggleTeachingMode(mode.value)}
            >
              <span className="mode-icon">{mode.icon}</span>
              <span className="mode-label">{mode.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Course Categories */}
      {/* <div className="form-group">
        <label className="form-label required">Course Categories</label>
        <div className="category-select-grid">
          {PROGRAM_CATEGORIES.map((group) => {
            const isActive = formData.selectedCategories.includes(group.group);
            return (
              <button
                key={group.group}
                type="button"
                className={`category-chip ${isActive ? "selected" : ""}`}
                onClick={() => toggleCategory(group.group)}
              >
                {isActive && <span className="chip-check">✓</span>}
                {group.group}
              </button>
            );
          })}
        </div>
      </div> */}
      {/* Course Categories */}
      <div className="form-group">
        <label className="form-label required">Course Categories</label>
        <div className="category-select-grid">
          {PROGRAM_CATEGORIES.filter((g) =>
            SELECTABLE_PROGRAM_CATEGORIES.includes(g.group),
          ).map((group) => {
            const isActive = formData.selectedCategories.includes(group.group);
            const atMax = formData.selectedCategories.length >= 3;
            return (
              <button
                key={group.group}
                type="button"
                className={`category-chip ${isActive ? "selected" : ""} ${
                  !isActive && atMax ? "disabled" : ""
                }`}
                onClick={() => toggleCategory(group.group)}
                disabled={!isActive && atMax}
              >
                {isActive && <span className="chip-check">✓</span>}
                {group.group}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Category Details Blocks */}
      {formData.selectedCategories.map((groupName) => {
        const details = formData.categoryDetails[groupName] || {
          courses: [],
          rate: "",
          period: "hour",
        };

        return (
          <div
            key={groupName}
            className="category-block"
            style={{
              border: "1px solid #ddd",
              padding: "15px",
              borderRadius: "8px",
              marginBottom: "20px",
            }}
          >
            <h4>{groupName}</h4>

            {/* Courses */}
            <div className="form-group">
              <label>Courses (max 5)</label>
              <div className="checkbox-group">
                {PROGRAM_CATEGORIES.find(
                  (g) => g.group === groupName,
                )?.items.map((course) => (
                  <label key={course} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={details.courses.includes(course)}
                      onChange={() => {
                        const newCourses = details.courses.includes(course)
                          ? details.courses.filter((c) => c !== course)
                          : [...details.courses, course].slice(0, 5);

                        updateCategoryDetail(groupName, {
                          courses: newCourses,
                        });
                      }}
                    />
                    {course}
                  </label>
                ))}
              </div>
            </div>

            {/* Pricing */}
            <div className="form-group">
              <label>Pricing for {groupName}</label>
              <div className="rate-input-group">
                <select
                  value={details.period}
                  onChange={(e) =>
                    updateCategoryDetail(groupName, { period: e.target.value })
                  }
                >
                  <option value="hour">Per Hour</option>
                  <option value="day">Per Day</option>
                  <option value="week">Per Week</option>
                  <option value="month">Per Month</option>
                  <option value="session">Per Session</option>
                </select>
                <input
                  type="number"
                  value={details.rate}
                  placeholder="Amount"
                  onChange={(e) =>
                    updateCategoryDetail(groupName, { rate: e.target.value })
                  }
                />
              </div>
            </div>
          </div>
        );
      })}
      {/* Free Intro/Trial Session */}
      <div className="form-group">
        <label className="form-label">
          Do you offer a free Intro/First Session?
        </label>
        <div className="intro-session-toggle">
          <label>
            <input
              type="radio"
              name="offersIntroSession"
              checked={formData.offersIntroSession === true}
              onChange={() =>
                setFormData((prev) => ({ ...prev, offersIntroSession: true }))
              }
            />
            Yes
          </label>
          <label>
            <input
              type="radio"
              name="offersIntroSession"
              checked={formData.offersIntroSession === false}
              onChange={() =>
                setFormData((prev) => ({
                  ...prev,
                  offersIntroSession: false,
                }))
              }
            />
            No
          </label>
        </div>
      </div>

      {/* Location */}
      <LocationInput formData={formData} setFormData={setFormData} />
      {/* Actions */}
      <div className="form-actions">
        <button onClick={onCancel} className="edit-cancel-btn">
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="edit-primary-btn"
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>
      <StatusModal
        open={statusModal.open}
        type={statusModal.type}
        message={statusModal.message}
        subtext={statusModal.subtext}
        onClose={() => {
          setStatusModal({ ...statusModal, open: false });
          if (statusModal.type === "success") onCancel();
        }}
      />
    </div>
  );
};

export default ProgramForm;
